import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "semantic-ui-react";

import { CarNoteModal, DMSNotes, DetailsCard, Dropdown, NoteRow, NoteTypeIcon, useCan } from "components";
import { CustomerNoteModal, CustomerNoteRow } from "components/CustomerModal/components/CustomerNotes/components";
import { APPOINTMENT_NOTE_TYPES, CAR_NOTE_TYPE, CUSTOMER_TYPES, STATUS_IDENTIFIERS } from "models";
import { AppointmentNoteModal } from "modules/AppointmentDetails/components";
import "modules/AppointmentDetails/components/AppointmentNotes/AppointmentNotes.scss";
import { useNoteTypeOptions } from "modules/AppointmentDetails/components/AppointmentNotes/hooks";
import { useAppointmentLocation } from "modules/AppointmentDetails/hooks";

import type { SectionMenu } from "components";
import type { Appointment, AppointmentNote, CarNote, CustomerNote } from "models";

type AppointmentNotesProps = {
  appointment: Appointment;
  isExpanded: boolean;
  sectionMenu: SectionMenu;
};

const getNotes = (appointment: Appointment) => ({
  importantNotes:
    appointment.notes?.filter(note =>
      [APPOINTMENT_NOTE_TYPES.Main, APPOINTMENT_NOTE_TYPES.CallCustomer, APPOINTMENT_NOTE_TYPES.HighVoltageWarning].includes(note.appointment_note_type_id)
    ) ?? [],
  receptionistRemarks:
    appointment.notes?.filter(
      note =>
        ![
          APPOINTMENT_NOTE_TYPES.Main,
          APPOINTMENT_NOTE_TYPES.CallCustomer,
          APPOINTMENT_NOTE_TYPES.DMS,
          APPOINTMENT_NOTE_TYPES.ReplacementVehicle,
          APPOINTMENT_NOTE_TYPES.HighVoltageWarning
        ].includes(note.appointment_note_type_id)
    ) ?? [],
  defaultCarNotes: appointment.car?.notes?.filter(note => note.visible_on_wo && note.car_note_type_id !== CAR_NOTE_TYPE.DMS) ?? [],
  customerOwnerNotes: appointment.customer_owner?.notes?.filter(note => note.visible_on_wo) ?? [],
  customerContractNotes: appointment.customer_contract?.notes?.filter(note => note.visible_on_wo) ?? [],
  customerDriverNotes: appointment.customer_driver?.notes?.filter(note => note.visible_on_wo) ?? [],
  dmsNotes: appointment.notes?.filter(note => [APPOINTMENT_NOTE_TYPES.DMS, APPOINTMENT_NOTE_TYPES.ReplacementVehicle].includes(note.appointment_note_type_id)) ?? [],
  dmsCarNotes: appointment.car?.notes?.filter(note => note.car_note_type_id === CAR_NOTE_TYPE.DMS) ?? []
});

export const getNotesCount = (appointment?: Appointment) => (appointment ? Object.values(getNotes(appointment)).reduce((count, note) => count + note.length, 0) : 0);

//TODO: Revisit this to refactor, it's messy, with the APPOINTMENT_NOTE_TYPES
export const AppointmentNotes = ({ appointment, sectionMenu, isExpanded }: AppointmentNotesProps) => {
  const { t } = useTranslation();

  const [showCardDetails, setShowCardDetails] = useState(isExpanded);
  const [selectedCarNote, setSelectedCarNote] = useState<CarNote | null>(null);
  const [selectedAppointmentNote, setSelectedAppointmentNote] = useState<AppointmentNote | null>(null);
  const [selectedCustomerNote, setSelectedCustomerNote] = useState<CustomerNote | null>(null);
  const [noteType, setNoteType] = useState<APPOINTMENT_NOTE_TYPES | null>(null);

  const appointmentLocation = useAppointmentLocation(appointment);
  const { noteTypeOptions } = useNoteTypeOptions(appointment.appointment_status_identifier);
  const canCreateNotes = useCan("create", ["appointment-notes", "appointment-notes-attachments", "appointment-notes-wo", "appointment-notes-parking-location"]);

  useEffect(() => {
    setShowCardDetails(isExpanded);
  }, [isExpanded]);

  useEffect(() => {
    setNoteType(selectedAppointmentNote?.appointment_note_type_id ?? null);
  }, [selectedAppointmentNote]);

  useEffect(() => {
    if (
      noteType &&
      [
        APPOINTMENT_NOTE_TYPES.Bill,
        APPOINTMENT_NOTE_TYPES.Main,
        APPOINTMENT_NOTE_TYPES.RecurringCar,
        APPOINTMENT_NOTE_TYPES.CallCustomer,
        APPOINTMENT_NOTE_TYPES.Attachment,
        APPOINTMENT_NOTE_TYPES.ParkingLocation,
        APPOINTMENT_NOTE_TYPES.NoDrive
      ].includes(noteType)
    ) {
      const note = appointment.notes?.find(note => note.appointment_note_type_id === noteType);

      if (note) setSelectedAppointmentNote(note);
    }
  }, [noteType]);

  if (!noteTypeOptions.length) return null;

  const toggleCardDetails = () => setShowCardDetails(prevState => !prevState);
  const handleSelectCarNote = (note: CarNote | null) => setSelectedCarNote(note);
  const handleSelectAppointmentNote = (note: AppointmentNote | null) => setSelectedAppointmentNote(note);

  const handleUnselectNote = () => {
    setNoteType(null);
    setSelectedAppointmentNote(null);
    setSelectedCarNote(null);
    setSelectedCustomerNote(null);
  };

  const { importantNotes, receptionistRemarks, defaultCarNotes, customerOwnerNotes, customerContractNotes, customerDriverNotes, dmsNotes, dmsCarNotes } =
    getNotes(appointment);
  const hasCustomerNotes = customerOwnerNotes.length > 0 || customerContractNotes.length > 0 || customerDriverNotes.length > 0;
  const showAppointmentNotes = importantNotes.length > 0 || hasCustomerNotes || (showCardDetails && (receptionistRemarks.length > 0 || defaultCarNotes.length > 0));

  return (
    <DetailsCard
      sectionMenuId={sectionMenu.id}
      title={sectionMenu.count ? `${sectionMenu.title} (${sectionMenu.count})` : sectionMenu.title}
      icon={sectionMenu.icon}
      isExpanded={showCardDetails}
      showExpandArrow={false}
      rightContent={
        appointment.appointment_status_identifier !== STATUS_IDENTIFIERS.CanceledStatus && canCreateNotes ? (
          <>
            <Icon className={`chevron ${showCardDetails ? "down" : "right"} pointer`} onClick={toggleCardDetails} />
            <Dropdown
              trigger={
                <div className="add-note-container">
                  <Icon className="plus" />
                </div>
              }
              upward={false}
              icon={null}
              selectOnBlur={false}
              value={noteType ?? -1}
              options={noteTypeOptions.map(option => ({
                ...option,
                icon: () => {
                  if (option.value === APPOINTMENT_NOTE_TYPES.BackOrder) return <div className="custom-back-order-dropdown-icon">BO</div>;
                  return <NoteTypeIcon type={option.value} />;
                }
              }))}
              onChange={noteType => setNoteType(noteType ?? null)}
            />
          </>
        ) : null
      }
    >
      <div className="AppointmentNotes">
        {showAppointmentNotes && (
          <div className="appointment-notes-content">
            {importantNotes.length > 0 &&
              importantNotes.map(note => <NoteRow appointmentNote={note} onSelectNote={() => handleSelectAppointmentNote(note)} key={note.id} />)}

            {receptionistRemarks.length > 0 && (
              <div className="receptionist-remarks-container">
                {receptionistRemarks.map(note => (
                  <NoteRow appointmentNote={note} onSelectNote={() => handleSelectAppointmentNote(note)} key={note.id} />
                ))}
              </div>
            )}

            {defaultCarNotes.length > 0 &&
              defaultCarNotes.map(note => (
                <div key={note.id} className="car-note-container">
                  <NoteRow carNote={note} isCarNote onSelectNote={() => handleSelectCarNote(note)} key={note.id} />
                </div>
              ))}

            <div className={hasCustomerNotes && (defaultCarNotes.length > 0 || importantNotes.length > 0 || receptionistRemarks.length > 0) ? "car-note-container" : ""}>
              {customerOwnerNotes.length > 0 &&
                customerOwnerNotes.map(note => (
                  <CustomerNoteRow
                    t={t}
                    isCustomerNoteModal={false}
                    customerNote={note}
                    key={note.id}
                    onSelectNote={() => setSelectedCustomerNote(note)}
                    customerType={CUSTOMER_TYPES.Owner}
                  />
                ))}

              {customerContractNotes.length > 0 &&
                customerContractNotes.map(note => (
                  <CustomerNoteRow
                    t={t}
                    isCustomerNoteModal={false}
                    customerNote={note}
                    key={note.id}
                    onSelectNote={() => setSelectedCustomerNote(note)}
                    customerType={CUSTOMER_TYPES.Contractor}
                  />
                ))}

              {customerDriverNotes.length > 0 &&
                customerDriverNotes.map(note => (
                  <CustomerNoteRow
                    t={t}
                    isCustomerNoteModal={false}
                    customerNote={note}
                    key={note.id}
                    onSelectNote={() => setSelectedCustomerNote(note)}
                    customerType={CUSTOMER_TYPES.Driver}
                  />
                ))}
            </div>
          </div>
        )}

        {dmsNotes.length + dmsCarNotes.length > 0 && showCardDetails && (
          <div className="dms-remarks-container">
            <DMSNotes dmsNotes={dmsNotes} dmsCarNotes={dmsCarNotes} licensePlate={appointment.car?.reg_nr} />
          </div>
        )}

        <AppointmentNoteModal
          appointment={appointment}
          createAppointmentNoteType={noteType}
          selectedAppointmentNote={selectedAppointmentNote}
          onUnselectNote={handleUnselectNote}
        />

        {!!appointment.customer_owner?.notes?.length && (
          <CustomerNoteModal
            open={Boolean(selectedCustomerNote)}
            customer={appointment.customer_owner}
            selectedCustomerNote={selectedCustomerNote}
            location={appointmentLocation}
            onUnselectCustomerNote={handleUnselectNote}
          />
        )}

        {!!appointment.customer_driver?.notes?.length && (
          <CustomerNoteModal
            open={Boolean(selectedCustomerNote)}
            customer={appointment.customer_driver}
            selectedCustomerNote={selectedCustomerNote}
            location={appointmentLocation}
            onUnselectCustomerNote={handleUnselectNote}
          />
        )}

        {!!appointment.customer_contract?.notes?.length && (
          <CustomerNoteModal
            open={Boolean(selectedCustomerNote)}
            customer={appointment.customer_contract}
            selectedCustomerNote={selectedCustomerNote}
            location={appointmentLocation}
            onUnselectCustomerNote={handleUnselectNote}
          />
        )}

        {!!selectedCarNote && appointment.car && (
          <CarNoteModal
            car={appointment.car}
            location={appointmentLocation}
            selectedNote={selectedCarNote}
            onUnselectNote={handleUnselectNote}
            onSetSelectedCarNote={handleSelectCarNote}
          />
        )}
      </div>
    </DetailsCard>
  );
};
